import Prompt from 'Components/tasks/Prompt/Prompt'
import styles from 'src/components/tasks/Prompt/Prompt.module.less'
import classes from './common/PromptGeneration/PromptGeneration.module.less'
import {
    ContainerDataType,
    ContainerType,
    CustomDataType,
    TaskAdditionalInfo,
    TaskMode,
    TurnData,
    TurnDataType,
} from 'Types/Tasks.types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Loader } from 'Components/common/Loader/Loader'
import { CreationMode } from 'Tasks/CustomTask/UI/CreationMode/CreationMode'
import { ReviewMode } from 'Tasks/CustomTask/UI/ReviewMode/ReviewMode'
import { PromptGeneration } from './common/PromptGeneration/PromptGeneration'
import Flex from 'antd/lib/flex'
import { ProjectFeatureFlags } from 'HTTPClients/projects.client'
import { PromptGenerationWithContext } from './common/PromptGenerationWithContext/PromptGenerationWithContext'
import TextArea from 'antd/lib/input/TextArea'
import { CustomForm, PromptExtraInfo } from 'Types/CustomTask.types'
import Input from 'antd/lib/input'
import MarkdownRenderer from 'Components/common/MarkdownRenderer/MarkdownRenderer'
import { UpdatableReactElement } from 'shared/utils/react.utils'
import { IPlugin } from 'Core/PluginCore/BasePlugin'
import { LiveFeedbackContextType } from 'Types/LiveFeedback.types'

export type CustomTaskUiProps = {
    getOutputDetails: () => ContainerDataType<ContainerType, CustomDataType> | undefined
    keyBindingActions: (event: KeyboardEvent) => void
    featureFlags?: ProjectFeatureFlags['feature_flags']
    taskMode: TaskMode
    taskAdditionalInfo: TaskAdditionalInfo
    isLoading: () => boolean
    turnData: () =>
        | {
              data: TurnData
              turnNumber: number
              retryCount: number
          }
        | undefined
    onSubmitGenerateResponseForm: (turnDataType: TurnDataType) => void
    onRetryTurn: () => void
    onSubmitTurnData: (
        type: 'TURN_ANNOTATION' | 'CORRECT_ANSWER',
        firstIncorrectStep?: number | null
    ) => void
    onPromptRevisionCreation?: (
        type: 'PROMPT_REVISION' | 'RETRY_NON_TURN'
    ) => Promise<ReadableStreamDefaultReader<Uint8Array<ArrayBufferLike>> | undefined>
    onContainerUpdate?: (container: ContainerDataType<ContainerType.Custom, CustomDataType>) => void
    isReadOnly: () => boolean
    getPrompt: () => string
    getPromptInstructions: () => string
    getPromptExtraInfo: () => PromptExtraInfo
    isPromptGenerationAllowed: () => boolean
    setForms: (forms: CustomForm) => void
    installedPlugins: IPlugin[]
    attributesUI?: UpdatableReactElement
    getQuestion: () => string
    setIsLiveFeedbackChecked: () => void
    liveFeedbackContext: LiveFeedbackContextType
    areAttributesValid: () => Promise<boolean>
}

export const CustomTaskUi = (props: CustomTaskUiProps) => {
    const {
        getOutputDetails,
        featureFlags,
        taskMode,
        taskAdditionalInfo,
        isLoading,
        turnData,
        onSubmitGenerateResponseForm,
        onRetryTurn,
        onSubmitTurnData,
        onPromptRevisionCreation,
        isReadOnly: isReadOnlyProps,
        getPrompt,
        getPromptInstructions,
        getPromptExtraInfo,
        isPromptGenerationAllowed,
        setForms,
        installedPlugins,
        attributesUI,
        getQuestion,
        setIsLiveFeedbackChecked,
        liveFeedbackContext,
        areAttributesValid,
    } = props
    const [isGeneratedPromptValid, setIsGeneratedPromptValid] = useState(false)
    const divRef = useRef<HTMLDivElement>(null)
    const [modelResponseExtraInfo, setModelResponseExtraInfo] = useState<{
        totalModelResponsesOnPromptRevision: number
        modelResponseFeedbackCode: { value: number; label: string }[]
        modelResponse: string
    }>({
        totalModelResponsesOnPromptRevision: 0,
        modelResponseFeedbackCode: [],
        modelResponse: '',
    })

    const isReadOnly = isReadOnlyProps()

    useEffect(() => {
        if (getOutputDetails()?.data?.data?.response?.main_info?.value?.prompt) {
            setIsGeneratedPromptValid(true)
        }
    }, [getOutputDetails])

    const content = useMemo(() => {
        if (taskMode === TaskMode.Maker || taskMode === TaskMode.Rework) {
            const turnDataType = isGeneratedPromptValid
                ? 'PROMPT_FINALIZATION'
                : 'PROMPT_ANNOTATION'
            const showCreationMode =
                turnData() ||
                (isPromptGenerationAllowed() && isGeneratedPromptValid) ||
                !isPromptGenerationAllowed()

            return showCreationMode ? (
                <CreationMode
                    featureFlags={featureFlags}
                    turnData={turnData}
                    onSubmitData={onSubmitTurnData}
                    onSubmitGenerateResponseForm={() => onSubmitGenerateResponseForm(turnDataType)}
                    onRetryTurn={onRetryTurn}
                    setForms={setForms}
                    modelResponseExtraInfo={modelResponseExtraInfo}
                    attributesUI={attributesUI}
                    installedPlugins={installedPlugins}
                    question={getQuestion()}
                    setIsLiveFeedbackChecked={setIsLiveFeedbackChecked}
                    outputStartState={getOutputDetails()?.data?.data}
                    liveFeedbackContext={liveFeedbackContext}
                    areAttributesValid={areAttributesValid}
                    onContainerUpdate={props.onContainerUpdate}
                />
            ) : null
        } else if (taskMode === TaskMode.Reviewer || taskMode === TaskMode.QC) {
            return (
                <ReviewMode
                    outputResponse={getOutputDetails()?.data?.data.response}
                    setForms={setForms}
                    isReadOnly={isReadOnly}
                    featureFlags={featureFlags}
                    attributesUI={attributesUI}
                    installedPlugins={installedPlugins}
                    question={getQuestion()}
                    setIsLiveFeedbackChecked={setIsLiveFeedbackChecked}
                    liveFeedbackContext={liveFeedbackContext}
                    areAttributesValid={areAttributesValid}
                />
            )
        }

        return null
    }, [
        attributesUI,
        featureFlags,
        getOutputDetails,
        getQuestion,
        installedPlugins,
        isGeneratedPromptValid,
        isPromptGenerationAllowed,
        isReadOnly,
        modelResponseExtraInfo,
        onRetryTurn,
        onSubmitGenerateResponseForm,
        onSubmitTurnData,
        setForms,
        setIsLiveFeedbackChecked,
        taskMode,
        turnData,
        liveFeedbackContext,
        areAttributesValid,
        props.onContainerUpdate,
    ])

    const loader = useMemo(() => {
        if (isLoading()) {
            return (
                <Loader title={"Loading will take up to 1 minute. Don't refresh. "} width="480px" />
            )
        }

        return null
    }, [isLoading])

    const prompt = getPrompt()
    const promptExtraInfo = getPromptExtraInfo()

    const promptComponent = useMemo(() => {
        if (isPromptGenerationAllowed()) {
            const contextDocumentPageNumber = promptExtraInfo.promptContextDocumentPageNumber
                ? promptExtraInfo.promptContextDocumentPageNumber.split(',').map(pageNumber => ({
                      pageNumber: pageNumber.trim(),
                  }))
                : []
            if (featureFlags?.lmf_project) {
                return (
                    <PromptGenerationWithContext
                        initialValues={{
                            prompt,
                            context: promptExtraInfo.promptContext ?? '',
                            contextDocumentUrl: promptExtraInfo.promptContextDocumentUrl ?? '',
                            contextDocumentName: promptExtraInfo.promptContextDocumentName ?? '',
                            contextDocumentPageNumber,
                        }}
                        promptInstruction={getPromptInstructions()}
                        setForms={setForms}
                        onPromptRevisionCreation={onPromptRevisionCreation!}
                        setIsGeneratedPromptValid={setIsGeneratedPromptValid}
                        setModelResponseExtraInfo={setModelResponseExtraInfo}
                        installedPlugins={installedPlugins}
                        outputStartState={getOutputDetails()?.data?.data}
                        onContainerUpdate={props.onContainerUpdate}
                    />
                )
            }
            return (
                <PromptGeneration
                    featureFlags={featureFlags}
                    initialValues={{
                        prompt,
                        options: promptExtraInfo.promptOptions,
                        context: promptExtraInfo.promptContext ?? '',
                        externalModelName: promptExtraInfo.externalModelName ?? '',
                        externalModelResponse: promptExtraInfo.externalModelResponse ?? '',
                        contextDocumentUrl: promptExtraInfo.promptContextDocumentUrl ?? '',
                    }}
                    setModelResponseExtraInfo={setModelResponseExtraInfo}
                    promptInstruction={getPromptInstructions()}
                    setForms={setForms}
                    onPromptRevisionCreation={onPromptRevisionCreation!}
                    setIsGeneratedPromptValid={setIsGeneratedPromptValid}
                    installedPlugins={installedPlugins}
                    outputStartState={getOutputDetails()?.data?.data}
                    onContainerUpdate={props.onContainerUpdate}
                />
            )
        }
        return (
            <Flex vertical>
                <Prompt
                    title={'INPUT'}
                    content={{
                        value: prompt,
                        value_type: 'STRING',
                    }}
                    taskInfo={taskAdditionalInfo}
                />
                <ol type="A">
                    {promptExtraInfo.promptOptions?.map((option, index) => (
                        <li key={index}>{option}</li>
                    ))}
                </ol>
                <Flex vertical className={styles.prompt}>
                    <b>Question Markdown View: </b>
                    <div className={classes.previewContainer}>
                        {' '}
                        <MarkdownRenderer escapeBackslashses={true}>{prompt}</MarkdownRenderer>
                    </div>
                </Flex>
                <Flex vertical gap={8} className={styles['prompt']}>
                    {promptExtraInfo.promptContextDocumentUrl && (
                        <div>
                            <b>Link of the reference document: </b>
                            <a href={promptExtraInfo.promptContextDocumentUrl} target="_blank">
                                {promptExtraInfo.promptContextDocumentUrl}
                            </a>{' '}
                        </div>
                    )}
                    {promptExtraInfo.promptContext && (
                        <div>
                            <b>Reference Content: </b>
                            <TextArea rows={15} value={promptExtraInfo.promptContext} disabled />
                        </div>
                    )}
                    {promptExtraInfo.promptContextDocumentName && (
                        <div>
                            <b>Name of reference document: </b>
                            <Input value={promptExtraInfo.promptContextDocumentName} disabled />
                        </div>
                    )}
                    {promptExtraInfo.promptContextDocumentPageNumber && (
                        <div>
                            <b>Page no. of reference content in document: </b>
                            <div>{promptExtraInfo.promptContextDocumentPageNumber}</div>
                        </div>
                    )}
                    {promptExtraInfo.externalModelName && (
                        <div>
                            <b>External model name: </b>
                            <div>{promptExtraInfo.externalModelName}</div>
                        </div>
                    )}
                    {promptExtraInfo.externalModelResponse && (
                        <div>
                            <b>External model response: </b>
                            <TextArea
                                rows={8}
                                value={promptExtraInfo.externalModelResponse}
                                disabled
                            />
                        </div>
                    )}
                </Flex>
            </Flex>
        )
    }, [
        featureFlags,
        getOutputDetails,
        getPromptInstructions,
        installedPlugins,
        isPromptGenerationAllowed,
        onPromptRevisionCreation,
        prompt,
        promptExtraInfo.externalModelName,
        promptExtraInfo.externalModelResponse,
        promptExtraInfo.promptContext,
        promptExtraInfo.promptContextDocumentName,
        promptExtraInfo.promptContextDocumentPageNumber,
        promptExtraInfo.promptContextDocumentUrl,
        promptExtraInfo.promptOptions,
        setForms,
        taskAdditionalInfo,
        props.onContainerUpdate,
    ])

    return (
        <div className="summarize-root" data-testid="generation-task-ui-wrapper">
            <div ref={divRef}>
                {promptComponent}
                {loader}
                {content}
            </div>
        </div>
    )
}
